import { Button } from '@/components/ui/button';
import { AvatarWithNotification } from '@/components/ui/avatar-with-notification';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, User, Baby } from 'lucide-react';
import { ImageUploader } from '@/components/ImageUploader';
import { calculateAge } from '@/lib/utils';
import { PersonProfile } from '@/hooks/usePersonProfiles';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface SimpleContactFormatProps {
  person: PersonProfile;
  onEdit: (person: PersonProfile) => void;
  onDelete: (personId: string) => void;
  onUpdate?: () => void;
}

export const SimpleContactFormat = ({ 
  person, 
  onEdit, 
  onDelete, 
  onUpdate 
}: SimpleContactFormatProps) => {
  const { toast } = useToast();

  const handleAvatarUpdate = async (avatarUrl: string) => {
    try {
      const urlWithCache = `${avatarUrl}?t=${Date.now()}`;
      
      // For adults with profile_id, update profiles table for proper synchronization
      if (person.profile_id && (person.family_role === 'parent' || person.family_role === 'spouse')) {
        const { error } = await supabase
          .from('profiles')
          .update({ avatar_url: urlWithCache })
          .eq('id', person.profile_id);

        if (error) throw error;
      } else {
        // For children or adults without profile_id, update person_profiles
        const { error } = await supabase
          .from('person_profiles')
          .update({ avatar_url: urlWithCache })
          .eq('id', person.id);

        if (error) throw error;
      }
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour la photo.",
        variant: "destructive",
      });
    }
  };

  const getIcon = (person: PersonProfile) => {
    if (person.family_role === 'son' || person.family_role === 'daughter' || person.family_role === 'child') {
      return person.date_of_birth && calculateAge(person.date_of_birth) < 13 ? Baby : User;
    }
    return User;
  };

  const IconComponent = getIcon(person);

  return (
    <div className="flex items-center gap-3 py-2">
      <ImageUploader
        currentImageUrl={person.avatar_url || ''}
        onImageUploaded={handleAvatarUpdate}
        bucketPath={`person-${person.id}`}
        size="sm"
      >
        <AvatarWithNotification
          src={person.avatar_url}
          alt={person.first_name}
          fallback={<IconComponent className="h-4 w-4" />}
          className="h-8 w-8 cursor-pointer hover:opacity-80 transition-opacity text-xs"
          personId={person.id}
          profileId={person.profile_id}
        />
      </ImageUploader>
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">{person.first_name} {person.last_name}</span>
          {person.emergency_contact && (
            <Badge variant="destructive" className="text-xs">
              Urgence
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {person.date_of_birth && (
            <span>{calculateAge(person.date_of_birth)} ans</span>
          )}
        </div>
      </div>
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(person)}
        >
          <Edit className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(person.id)}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};